import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clubAPI from "../../../api/clubAPI";
import swalDialog from "../../../helpers/swalErrorPopup";
import { isLoadChanger } from "../../../store/mainSlice";

const IDLE = { status: "idle", ids: null };

// The endpoint's envelope isn't verified against a live response yet. Its
// ID-based sibling returns Data as a JSON string wrapping ClubsList, so accept
// that plus the two other plausible shapes rather than blanking the list.
const parseClubIds = (rawData) => {
  const parsed = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
  const list = Array.isArray(parsed) ? parsed : (parsed?.ClubsList ?? parsed?.ClubIds ?? []);
  return Array.isArray(list) ? new Set(list) : new Set();
};

/**
 * Resolves a static category label to the set of club IDs the backend allows,
 * via GetMobileClubsIdsBySubCategory (which takes the name, not an ID).
 *
 * `status` is what stops the caller flashing the unfiltered list: "loading"
 * means a category is selected but its clubs aren't known yet, which is NOT the
 * same as having no filter.
 *
 * @param {string} subCategoryName - falsy for "all activities", i.e. no filter
 * @returns {{status: "idle"|"loading"|"ready"|"error", allowedClubIds: Set<number>|null}}
 */
export default function useSubCategoryClubs(subCategoryName) {
  const dispatch = useDispatch();
  const binID = useSelector((state) => state.userSlice.userData?.BinID);
  const token = useSelector((state) => state.userSlice.mainUserData?.Token);
  const id = useSelector((state) => state.userSlice.mainUserData?.ID);
  const phone = useSelector((state) => state.userSlice.mainUserData?.Phone);

  const [state, setState] = useState(IDLE);

  // Generation counter: a slow response for a category the user has already
  // moved off must not overwrite the newer selection (same guard as
  // AddressSelect's requestIdRef).
  const runIdRef = useRef(0);

  useEffect(() => {
    // "All activities" — clear the filter without a request.
    if (!subCategoryName) {
      runIdRef.current++;
      setState(IDLE);
      return undefined;
    }
    if (!binID || !token || !id || !phone) return undefined;

    const runId = ++runIdRef.current;
    // Drops the previous category's ids immediately, so switching chips never
    // shows the old category's clubs under the new chip.
    setState({ status: "loading", ids: null });

    (async () => {
      try {
        dispatch(isLoadChanger(true));
        const response = await clubAPI.getClubsBySubCategory({ binID, token, id, phone, subCategoryName });
        if (runId !== runIdRef.current) return;
        if (response.data.Error !== 0) {
          throw { code: response.data.Error, mess: response.data.Message };
        }
        setState({ status: "ready", ids: parseClubIds(response.data.Data) });
      } catch (err) {
        if (runId !== runIdRef.current) return;
        // Falls back to unfiltered rather than empty, so a failed lookup doesn't
        // look like "no clubs match this category".
        setState({ status: "error", ids: null });
        swalDialog(err);
      } finally {
        dispatch(isLoadChanger(false));
      }
    })();

    return undefined;
  }, [subCategoryName, binID, token, id, phone, dispatch]);

  return { status: state.status, allowedClubIds: state.ids };
}
